import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone, Facebook, Instagram, Youtube, ExternalLink } from 'lucide-react';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const quickLinks = [
    { name: 'About Us', href: '/about' },
    { name: 'Why We Do What We Do', href: '/why-we-do' },
    { name: 'Statement of Faith', href: '/statement-of-faith' },
    { name: 'Prayer Requests', href: '/prayer' },
    { name: 'Contact', href: '/contact' }
  ];

  const ministries = [
    { name: 'Sermons', href: '/sermons' },
    { name: 'Bible Studies', href: '/bible-studies' },
    { name: 'Blog', href: '/blog' },
    { name: 'Community Outreach', href: '/outreach' },
    { name: 'Subscribe', href: '/subscribe' }
  ];

  const socialLinks = [
    { icon: Facebook, href: 'https://facebook.com/elbethelglobal', label: 'Facebook' },
    { icon: Instagram, href: 'https://instagram.com/elbethelglobal', label: 'Instagram' },
    { icon: Youtube, href: 'https://youtube.com/@elbethelglobal', label: 'YouTube' }
  ];

  return (
    <footer className="bg-gray-900 text-white pt-12 lg:pt-16 pb-6 lg:pb-8">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-8 lg:mb-12">
          {/* Ministry Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-3 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_f1a5c9b5-8f1b-4d34-88be-faeda192a0ed/artifacts/8ox9chkc_2.png"
                alt="El Bethel Church"
                className="w-10 lg:w-12 h-10 lg:h-12"
              />
              <div>
                <div className="font-bold text-base lg:text-lg font-display">EL BETHEL</div>
                <div className="text-sm text-gray-300 font-sans">CHURCH</div>
              </div>
            </div>
            <p className="text-gray-300 mb-6 leading-relaxed font-sans text-sm lg:text-base">
              A local church in Grand Prairie, Texas exalting Christ through expository preaching and authentic worship.
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((social, index) => {
                const Icon = social.icon;
                return (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-[#f17b21] transition-colors duration-300"
                    aria-label={social.label}
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Ministries */}
          <div>
            <h3 className="text-lg font-bold mb-4 font-display">Ministries</h3>
            <ul className="space-y-2">
              {ministries.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.href}
                    onClick={scrollToTop}
                    className="text-gray-300 hover:text-[#f17b21] transition-colors duration-300 text-sm lg:text-base font-sans"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-4 font-display">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.href}
                    onClick={scrollToTop}
                    className="text-gray-300 hover:text-[#f17b21] transition-colors duration-300 text-sm lg:text-base font-sans"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-4 font-display">Contact Us</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3 mb-4">
                <MapPin className="w-5 h-5 text-[#f17b21] flex-shrink-0 mt-1" />
                <div className="text-gray-300 font-sans text-sm lg:text-base">
                  <p className="font-semibold text-white">Join Us for Worship:</p>
                  <p>Grand Prairie, TX area</p>
                  <p className="text-xs text-gray-400 mt-1">We gather in a home setting for intimate worship.</p>
                  <a href="/visit" className="text-xs text-[#f17b21] hover:text-orange-300 mt-1 inline-block">Request location details →</a>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-[#f17b21] flex-shrink-0 mt-1" />
                <div className="text-gray-300 font-sans text-sm lg:text-base">
                  <p className="font-semibold text-white">Mailing Address:</p>
                  <p>765 W Westchester Pkwy #541221</p>
                  <p>Grand Prairie, TX 75052</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-[#f17b21] flex-shrink-0" />
                <a 
                  href="mailto:contact@elbethel.church"
                  className="text-gray-300 hover:text-[#f17b21] transition-colors duration-300 font-sans text-sm lg:text-base"
                >
                  contact@elbethel.church
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* El Bethel Ministries */}
        <div className="border-t border-gray-800 pt-6 lg:pt-8 mb-6 lg:mb-8">
          <h3 className="text-lg font-bold mb-6 text-center font-display">El Bethel Ministries</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <div className="bg-gray-800 rounded-lg p-4">
              <a
                href="https://elbethel.online"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#f17b21] hover:text-[#d86919] font-bold text-lg mb-2 block transition-colors duration-300 font-display"
              >
                El Bethel Online →
              </a>
              <p className="text-gray-300 text-sm font-sans">
                Our digital ministry platform - watch services online, access sermon archives, and connect with believers worldwide through our online community.
              </p>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <a
                href="https://theblanket.us"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#f17b21] hover:text-[#d86919] font-bold text-lg mb-2 block transition-colors duration-300 font-display"
              >
                The Blanket Project →
              </a>
              <p className="text-gray-300 text-sm font-sans">
                Our community outreach ministry serving the Dallas metro area with blankets, food, and essential supplies - bringing warmth, hope, and dignity to those in need.
              </p>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <a
                href="https://christeverywhere.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#f17b21] hover:text-[#d86919] font-bold text-lg mb-2 block transition-colors duration-300 font-display"
              >
                Christ Everywhere →
              </a>
              <p className="text-gray-300 text-sm font-sans">
                Our sister organization - a digital Christian community platform connecting believers at work, school, church, and home for prayer, fellowship, and spiritual growth.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-400 text-sm mb-4 md:mb-0 font-sans">
              © 2025 El Bethel Church. All rights reserved.
            </div>
            <div className="flex space-x-6">
              <Link to="/privacy" onClick={scrollToTop} className="text-gray-400 hover:text-[#f17b21] text-sm transition-colors font-sans">
                Privacy Policy
              </Link>
              <Link to="/terms" onClick={scrollToTop} className="text-gray-400 hover:text-[#f17b21] text-sm transition-colors font-sans">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;